import { FC, FormEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@remix-run/react';
import cls from 'classnames';
import JSB from '@bridge/sharee';
import { Button, ButtonSize } from '@lemon8/design';
import InfoIcon from '@lemon8/design/lib/icons/icon_assets/exclamation_circle.svg';
import XMarkIcon from '@lemon8/design/lib/icons/icon_assets/xmark_circle_fill.svg';
import { useAtomValue, useSetAtom } from 'jotai';
import { $UserAgent } from '@lemon8/web-app-shared/atom';
import useCountDown from '~/utils/hooks/use-count-down';
import { useTranslation } from 'react-i18next';
import useSubmitEmail from '~/utils/fetch/use-submit-email';
import Nav from '~/components/nav';
import { EmailVerifyContextReducerKey, ReportProgress, useEmailVerifyReducer } from '~/atoms/email-verify';
import { $ToastAtom } from '~/atoms/simple-toast';
import { closeWebview } from '~/utils/webview';

import './style.scss';

const EmailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w+)+$/;

export const ReportBody: FC = () => {
  const { isInApp, isMobile } = useAtomValue($UserAgent);
  const submitEmail = useSubmitEmail();
  const navigate = useNavigate();
  const [state, dispatch] = useEmailVerifyReducer();
  const { email = '', lastSendCodeTime, progress } = state;
  const { time, reset, clear } = useCountDown();

  const showToast = useSetAtom($ToastAtom);

  const [value, setValue] = useState(email);
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  // 输入引起的 error
  const [inputError, setInputError] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    if (progress === ReportProgress.RESULT) {
      // 结果页直接退回 IP 页
      if (isInApp) {
        void closeWebview();
      } else {
        history.back();
      }
    } else {
      // 表单页 回退至 邮箱输入页
      dispatch({
        type: EmailVerifyContextReducerKey.UPDATE_PROGRESS,
        payload: {
          progress: ReportProgress.EMAIL_INPUT,
        },
      });
    }
  }, [progress, dispatch]);

  // 开启页面倒计时
  useEffect(() => {
    if (lastSendCodeTime > 0) {
      const left = Math.floor((Date.now() - lastSendCodeTime) / 1000);
      if (left <= 60 && left >= 0) {
        reset(60 - left);
      }
    }
    return clear;
  }, [lastSendCodeTime]);

  // 输入变化回调
  const handleInputChange = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      if (emailSubmitting) {
        return;
      }

      // 输入变化开始，清空 error
      setInputError(false);
      const nextValue = event.currentTarget.value;
      setValue(nextValue);

      if (!EmailRegex.test(nextValue)) {
        setInputError(true);
      }
    },
    [setValue],
  );

  // 清空输入框
  const handleClearClick = useCallback(() => {
    setValue('');
    setInputError(true);
  }, [setValue]);

  // 提交「发送邮箱验证码」申请
  const handleSubmit = async () => {
    dispatch({
      type: EmailVerifyContextReducerKey.UPDATE_SEND_TIME,
      payload: {
        isInApp,
      },
    });
    setEmailSubmitting(true);
    await submitEmail(value)
      .then(() => {
        dispatch({
          type: EmailVerifyContextReducerKey.UPDATE_EMAIL,
          payload: {
            email: value,
          },
        });
        navigate(`/h5/feoffline/report/email-verify/code${location.search}`);
      })
      .catch(() => {
        // 发送验证码失败
        showToast({
          time: Date.now(),
          content: t('code_sent_fail'),
        });
      })
      .finally(() => {
        setEmailSubmitting(false);
      });
  };

  const buttonDisabled = useMemo(() => {
    return inputError || time > 0 || value.length === 0;
  }, [inputError, time, value]);

  const buttonText = useMemo(() => {
    return t('send_verification_code') + (time > 0 ? ` ${time}s` : '');
  }, [time]);

  const buttonStatus = useMemo(() => {
    return emailSubmitting ? 'Loading' : buttonDisabled ? 'Disabled' : 'Default';
  }, [emailSubmitting, buttonDisabled]);

  useEffect(() => {
    JSB.view.setKeyboardVisible({ isVisible: true });
  }, []);

  // 阻止滚动
  useEffect(() => {
    const touchHandler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('touchmove', touchHandler, { passive: false });

    return () => {
      document.removeEventListener('touchmove', touchHandler);
    };
  }, []);

  return (
    <div className={cls('email-verify-input', { isPC: !isMobile })}>
      {!isMobile && <Nav isShowSwitcher={false} />}
      <h2>{t('verify_your_email')}</h2>
      <p className="email-verify-input-desc">{t('need_verify_your_email_address_before_make_report')}</p>
      <div className="email-verify-input-input">
        <input
          type="email"
          autoFocus
          value={value}
          placeholder={t('enter_your_email_address')}
          onChange={handleInputChange}
        />
        {Boolean(value.length) && <XMarkIcon onClick={handleClearClick} />}
      </div>
      <div className="email-verify-input-error">
        {inputError && (
          <div className="email-verify-input-error-inner">
            <InfoIcon />
            <p>{t('enter_valid_email_address')}</p>
          </div>
        )}
      </div>
      <Button
        className="email-verify-input-button"
        title={buttonText}
        variant="Primary"
        onClick={handleSubmit}
        size={ButtonSize.large}
        status={buttonStatus as any}
      />
    </div>
  );
};
